const MAX_RENDERED_ELEMENTS_PER_ITEM = 30;
const DEFAULT_POST_WIDTH_CM = 9;
const SCENE_REFERENCE_HEIGHT_CM = 180;
const SCENE_REFERENCE_HEIGHT_PX = 105;
const MIN_POST_HEIGHT_PX = 40;
const MIN_PANEL_HEIGHT_PX = 32;
const MIN_PANEL_WIDTH_PX = 20;
const MIN_BRACKET_INSET_PX = 7;
const BRACKET_INSET_OFFSET_PX = 8;

const SLOPE_SHAPES = ['slope', 'lattice-slope', 'arch-slope-lattice'];
const ARCH_CONVEX_SHAPES = [
  'arch-convex',
  'arch-convex-lattice',
  'arch-convex-full-lattice',
  'arch-convex-gate',
  'picket-convex',
  'picket-gate-convex',
];
const ARCH_DROP_SHAPES = [...ARCH_CONVEX_SHAPES, 'arch-concave-lattice', 'picket-concave'];

const WPC_PANEL_COLOR_PATTERNS = [
  ['smoke-white', /smoke\s*white/],
  ['charcoal', /charcoal/],
  ['anthrazit', /anthrazit/],
  ['antique', /antique/],
  ['beige', /beige/],
  ['mokka', /mokka/],
  ['teak', /teak/],
  ['grau', /grau|grey|gray/],
  ['gruen', /ral\s*6005|grün|gruen/],
  ['verzinkt', /feuerverz|verzinkt/],
  ['weiss', /wei(?:ss|ß)|white/],
];
const WPC_FRAME_COLOR_PATTERNS = [
  ['anthrazit', /anthrazit/],
  ['silber', /silber|silver/],
  ['gruen', /ral\s*6005|grün|gruen/],
  ['verzinkt', /feuerverz|verzinkt/],
];

const colorFromText = (text, patterns) => {
  const source = text || '';
  return patterns
    .map(([color, pattern], priority) => {
      const match = source.match(pattern);
      return match ? { color, index: match.index, priority } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.index - b.index || a.priority - b.priority)[0]?.color || '';
};

const isSlopeShape = (shape) => SLOPE_SHAPES.includes(shape);
const isArchConvexShape = (shape) => ARCH_CONVEX_SHAPES.includes(shape);
const hasArchDrop = (shape) => ARCH_DROP_SHAPES.includes(shape);
const cmToPx = (cm) => (cm / SCENE_REFERENCE_HEIGHT_CM) * SCENE_REFERENCE_HEIGHT_PX;

function parseFenceDimensions(title) {
  const source = title || '';
  const decimal = (value) => Number.parseFloat(String(value || '').replace(',', '.')) || 0;
  let match = source.match(
    /((?:\d+(?:[.,]\d+)?|[.,]\d+))\s*x\s*((?:\d+(?:[.,]\d+)?|[.,]\d+))(?:\s*\/\s*((?:\d+(?:[.,]\d+)?|[.,]\d+)))?\s*cm/i,
  );
  if (match) return { width: decimal(match[1]), height: decimal(match[2]), lowHeight: decimal(match[3]) };

  match = source.match(
    /\bB\s*((?:\d+(?:[.,]\d+)?|[.,]\d+))\s*x\s*H\s*((?:\d+(?:[.,]\d+)?|[.,]\d+))(?:\s*\/\s*((?:\d+(?:[.,]\d+)?|[.,]\d+)))?(?:\s*x\s*T\s*(?:\d+(?:[.,]\d+)?|[.,]\d+))?\s*mm/i,
  );
  if (match) {
    return {
      width: decimal(match[1]) / 10,
      height: decimal(match[2]) / 10,
      lowHeight: decimal(match[3]) / 10,
    };
  }

  match = source.match(
    /((?:\d+(?:[.,]\d+)?|[.,]\d+))\s*x\s*((?:\d+(?:[.,]\d+)?|[.,]\d+))(?:\s*\/\s*((?:\d+(?:[.,]\d+)?|[.,]\d+)))?\s*mm/i,
  );
  if (match) {
    return {
      width: decimal(match[1]) / 10,
      height: decimal(match[2]) / 10,
      lowHeight: decimal(match[3]) / 10,
    };
  }

  match = source.match(
    /((?:\d+(?:[.,]\d+)?|[.,]\d+))\s*x\s*((?:\d+(?:[.,]\d+)?|[.,]\d+))(?:\s*\/\s*((?:\d+(?:[.,]\d+)?|[.,]\d+)))?\s*m\b/i,
  );
  if (match) {
    return {
      width: decimal(match[1]) * 100,
      height: decimal(match[2]) * 100,
      lowHeight: decimal(match[3]) * 100,
    };
  }

  const namedLengthCm = (label) => {
    const namedMatch = source.match(new RegExp(
      `\\b${label}\\s*(?:ca\\.?\\s*)?((?:\\d+(?:[.,]\\d+)?|[.,]\\d+))\\s*(mm|cm|m)\\b`,
      'i',
    ));
    if (!namedMatch) return 0;
    const value = decimal(namedMatch[1]);
    if (namedMatch[2].toLowerCase() === 'mm') return value / 10;
    if (namedMatch[2].toLowerCase() === 'm') return value * 100;
    return value;
  };
  const width = namedLengthCm('Breite') || namedLengthCm('Rollenlänge');
  const height = namedLengthCm('Höhe');
  if (width && height) return { width, height, lowHeight: 0 };

  return { width: 0, height: 0, lowHeight: 0 };
}

function detectVisualStyles(typeText) {
  const styles = [];
  const isClassicoWood = /(?:^|[\s-])classico(?:[\s-]|$)/.test(typeText);
  if (/doppelstabmatte|stabmattenzaun|schmuckzaun/.test(typeText)) {
    styles.push('wire-mesh');
    if (/8\s*\/\s*6\s*\/\s*8/.test(typeText)) styles.push('wire-heavy');
    if (/(?:^|\s)caro(?:\s|$)/.test(typeText)) styles.push('wire-caro');
    else if (/(?:^|\s)rondo(?:\s|$)/.test(typeText)) styles.push('wire-rondo');
    if (/ral\s*6005|grün|gruen/.test(typeText)) styles.push('wire-green');
    else if (/ral\s*7016|anthrazit/.test(typeText)) styles.push('wire-anthracite');
    else if (/feuerverz|verzinkt/.test(typeText)) styles.push('wire-galvanized');
  } else if (!isClassicoWood && /glas|vetro/.test(typeText)) {
    styles.push('glass');
    if (/dekor\s*84|vollflächen/.test(typeText)) styles.push('glass-frosted');
    else if (/dekor\s*8[1-3]|streifen/.test(typeText)) styles.push('glass-striped');
    else styles.push('glass-clear');
  } else if (/weide|haselnuss|geflecht/.test(typeText)) {
    styles.push('woven');
  } else if (/schilf/.test(typeText)) {
    styles.push('reed');
  } else if (/rhombus/.test(typeText)) {
    styles.push('rhombus');
  } else if (/lattenzaun|halblatten/.test(typeText)) {
    styles.push('vertical-slat');
  } else if (/senkrechtzaun/.test(typeText)) {
    styles.push('pointed-picket');
  } else if (/lamellenzaun/.test(typeText)) {
    styles.push('horizontal-slat');
  } else if (/baumkante/.test(typeText)) {
    styles.push('rustic-board');
  }
  if (/(?:^|[\s-])freiburg(?:[\s-]|$)/.test(typeText)) styles.push('open-top-rails');
  if (/aluminium|(?:^|\s)alu(?:\s|-)/.test(typeText)) styles.push('metal');
  if (/kunststoff|pvc|ks-(?:zaun|lamellen)/.test(typeText)) styles.push('plastic');
  return styles;
}

// [left, right] edge height of an element. `highSide` picks which side of a slope is the tall
// one – it's only meaningful for slope shapes, ignored otherwise. Bögen/Stufen have a short side
// too, which counts as the low edge.
function edgesForShape(item, highSide) {
  if (isSlopeShape(item.shape)) return highSide === 'right' ? [item.lowHeight, item.height] : [item.height, item.lowHeight];
  if (isArchConvexShape(item.shape)) return [item.lowHeight, item.lowHeight];
  if (item.shape === 'step') return [item.height, item.lowHeight];
  return [item.height, item.height];
}

function detectShape(typeText, hasSlopeDimension, isWoodenGardenFence = false) {
  const isWollbachWpc = /wollbach|wpc[\s-]*steckzaun/.test(typeText);
  const hasLattice = /ranki|rankgitter|gitter/.test(typeText);
  const hasSlope = /ecke|schräg|schraeg/.test(typeText) || hasSlopeDimension;
  const doubleArchEndPattern = /doppelbogen[^,]*abschlu(?:ss|ß)|abschlu(?:ss|ß)[^,]*doppelbogen/;
  const isDoubleArchEnd = doubleArchEndPattern.test(typeText);
  const isDoubleArchTwo = /doppelbogen\s*2/.test(typeText);
  const isDoubleArchOne = /doppelbogen\s*1/.test(typeText);
  const isCrownArch = /(?:^|[\s-])krone(?:[\s-]|$)/.test(typeText);
  const isEndElement = /abschlu(?:ss|ß)/.test(typeText);
  const isArch = /(?:^|[\s-])bogen(?:[\s-]|$)/.test(typeText);
  const isPicketGate = /gartentor|vorgartentor|einzeltor|doppeltor|tür(?:element)?|tuer(?:element)?|(?:^|[\s-])tor(?:[\s-]|$)/.test(typeText);
  if (isWoodenGardenFence && isPicketGate) return isArch ? 'picket-gate-convex' : 'picket-gate';
  if (isWoodenGardenFence && /unterbogen|tiefbogen/.test(typeText)) return 'picket-concave';
  if (isWoodenGardenFence && /hochbogen/.test(typeText)) return 'picket-convex';
  if (isWoodenGardenFence) return 'picket';
  if (isWollbachWpc && isPicketGate) return 'wpc-gate';
  if (isWollbachWpc) return 'wpc-panel';
  if (isDoubleArchEnd) return 'arch-slope-lattice';
  if (isDoubleArchTwo) return 'arch-concave-lattice';
  if (isDoubleArchOne) return 'arch-convex-lattice';
  if (isCrownArch && hasLattice && isEndElement) return 'arch-convex-full-lattice';
  if (isCrownArch) return hasLattice ? 'arch-convex-lattice' : 'arch-convex';
  if (isArch && isPicketGate) return 'arch-convex-gate';
  if (isArch) return 'arch-convex';
  if (isPicketGate) return 'gate';
  if (hasLattice && hasSlope) return 'lattice-slope';
  if (hasLattice) return 'lattice';
  if (hasSlope) return 'slope';
  if (/stufe/.test(typeText)) return 'step';
  return 'standard';
}

// Drag-and-drop + keyboard reordering of panels within the fence scene. Kept independent of
// FenceConfigurator's pricing/accessory logic – it only ever touches an externally owned order.
class FenceSceneDragSort {
  constructor({ scene, getOrder, setOrder, onReorder }) {
    this.scene = scene;
    this.getOrder = getOrder;
    this.setOrder = setOrder;
    this.onReorder = onReorder;
    this.clearDragState();
    scene.addEventListener('pointerdown', (event) => this.pointerStart(event));
    scene.addEventListener('pointermove', (event) => this.pointerMove(event));
    scene.addEventListener('pointerup', (event) => this.pointerEnd(event));
    scene.addEventListener('pointercancel', () => this.clearDragState());
    scene.addEventListener('keydown', (event) => this.keyboardSort(event));
  }

  clearDragState() {
    this.scene.querySelectorAll('.is-dragging,.is-drop-before,.is-drop-after').forEach((item) => item.classList.remove('is-dragging', 'is-drop-before', 'is-drop-after'));
    this.draggedKey = '';
    this.dropTargetKey = '';
    this.dropAfter = false;
    this.pendingDragKey = '';
    this.dragPointerId = null;
  }

  pointerStart(event) {
    if (event.button !== 0 || !event.isPrimary) return;
    if (event.target.closest('[data-remove-fence-item]')) return;
    const panel = event.target.closest('[data-fence-key]');
    if (!panel) return;
    this.pendingDragKey = panel.dataset.fenceKey;
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    panel.setPointerCapture(event.pointerId);
  }

  pointerMove(event) {
    if (event.pointerId !== this.dragPointerId || !this.pendingDragKey) return;
    if (!this.draggedKey && Math.hypot(event.clientX - this.dragStartX, event.clientY - this.dragStartY) < 5) return;
    if (!this.draggedKey) {
      this.draggedKey = this.pendingDragKey;
      this.scene.querySelector(`[data-fence-key="${CSS.escape(this.draggedKey)}"]`)?.classList.add('is-dragging');
    }
    event.preventDefault();
    const target = [...this.scene.querySelectorAll('[data-fence-key]')]
      .filter((panel) => panel.dataset.fenceKey !== this.draggedKey)
      .sort((a, b) => {
        const aRect = a.getBoundingClientRect();
        const bRect = b.getBoundingClientRect();
        return Math.abs(event.clientX - (aRect.left + aRect.width / 2)) - Math.abs(event.clientX - (bRect.left + bRect.width / 2));
      })[0];
    this.setDropTarget(target, event.clientX);
  }

  pointerEnd(event) {
    if (event.pointerId !== this.dragPointerId) return;
    if (this.draggedKey) this.applyDrop();
    else this.clearDragState();
  }

  setDropTarget(panel, clientX) {
    if (!panel || panel.dataset.fenceKey === this.draggedKey) return;
    this.scene.querySelectorAll('.is-drop-before,.is-drop-after').forEach((item) => item.classList.remove('is-drop-before', 'is-drop-after'));
    const rect = panel.getBoundingClientRect();
    this.dropTargetKey = panel.dataset.fenceKey;
    this.dropAfter = clientX > rect.left + rect.width / 2;
    panel.classList.add(this.dropAfter ? 'is-drop-after' : 'is-drop-before');
  }

  applyDrop() {
    if (!this.draggedKey || !this.dropTargetKey || this.draggedKey === this.dropTargetKey) {
      this.clearDragState();
      return;
    }
    const order = this.getOrder().filter((key) => key !== this.draggedKey);
    const targetIndex = order.indexOf(this.dropTargetKey);
    order.splice(Math.max(0, targetIndex + (this.dropAfter ? 1 : 0)), 0, this.draggedKey);
    this.setOrder(order);
    this.clearDragState();
    this.onReorder();
  }

  keyboardSort(event) {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    if (event.target.closest('[data-remove-fence-item]')) return;
    const panel = event.target.closest('[data-fence-key]');
    if (!panel) return;
    const order = this.getOrder();
    const currentIndex = order.indexOf(panel.dataset.fenceKey);
    const nextIndex = currentIndex + (event.key === 'ArrowLeft' ? -1 : 1);
    if (nextIndex < 0 || nextIndex >= order.length) return;
    event.preventDefault();
    [order[currentIndex], order[nextIndex]] = [order[nextIndex], order[currentIndex]];
    this.setOrder(order);
    this.onReorder(panel.dataset.fenceKey);
  }
}

class FenceConfigurator extends HTMLElement {
  connectedCallback() {
    this.storageKey = `fence-configurator:v1:${window.location.pathname}`;
    this.isInsertFenceSystemType = this.dataset.insertFenceSystem === 'true';
    this.isWoodenGardenFenceType = this.dataset.woodenGardenFence === 'true';
    this.scene = this.querySelector('[data-fence-scene]');
    this.empty = this.querySelector('[data-fence-empty]');
    this.lengthOutput = this.querySelector('[data-fence-length]');
    this.priceOutput = this.querySelector('[data-fence-price]');
    this.deliveryTimeOutput = this.querySelector('[data-fence-delivery-time]');
    this.checksContainer = this.querySelector('[data-fence-checks]');
    this.wpcColorPicker = this.querySelector('[data-wpc-color-picker]');
    this.frameColorLabel = this.querySelector('[data-frame-color-label]');
    this.frameColorOptions = this.querySelector('[data-frame-color-options]');
    this.wpcColorButtons = [...this.querySelectorAll('[data-wpc-color]')];
    this.wpcPanelColorPicker = this.querySelector('[data-wpc-panel-color-picker]');
    this.wpcPanelColorLabel = this.querySelector('[data-wpc-panel-color-label]');
    this.wpcPanelColorOptions = this.querySelector('[data-wpc-panel-color-options]');
    this.wpcPanelColorButtons = [...this.querySelectorAll('[data-wpc-panel-color]')];
    this.postLengthPicker = this.querySelector('[data-post-length-picker]');
    this.postLengthSelect = this.querySelector('[data-post-length-select]');
    this.wirePostTypePicker = this.querySelector('[data-wire-post-type-picker]');
    this.wirePostTypeButtons = [...this.querySelectorAll('[data-wire-post-type]')];
    this.checkRows = {
      post: this.querySelector('[data-accessory-check="post"]'),
      bracket: this.querySelector('[data-accessory-check="bracket"]'),
      startEnd: this.querySelector('[data-accessory-check="start-end"]'),
      foundation: this.querySelector('[data-accessory-check="foundation"]'),
    };
    this.accessoryButtons = [...this.querySelectorAll('[data-add-required]')];
    this.cartButton = this.querySelector('[data-add-all-to-cart]');
    this.cartStatus = this.querySelector('[data-cart-status]');
    this.elementOrder = [];
    this.restoreState();
    this.wpcFrameColor ||= 'silber';
    this.wirePostType ||= 'tom';
    this.postLengthMm ||= 2400;
    if (this.postLengthSelect) this.postLengthSelect.value = String(this.postLengthMm);
    this.dispatchQuantityChanges(this.syncWpcPanelColor());
    this.onChange = (event) => {
      if (event.target.matches('[data-fence-config-item]')) {
        if (this.isSynchronizingWpc) return;
        this.dispatchQuantityChanges(this.syncWpcPanelColor(event.target));
        this.render();
        if (this.isInsertFenceConfiguration) {
          this.trimAccessoryToRequired(this.accessoryState?.postCarrier);
          this.render();
        }
        this.saveState();
      }
    };
    this.onQuantityClick = (event) => {
      if (event.target.closest('quantity-input')) {
        requestAnimationFrame(() => {
          this.render();
          this.saveState();
        });
        return;
      }

      const card = event.target.closest('.product-card-wrapper');
      const media = card?.querySelector('.card__media');
      // Dawn legt den Titel-Link per Pseudoelement über die gesamte Karte. Deshalb
      // ist event.target bei einem sichtbaren Bildklick nicht zwingend ein Kind von
      // .card__media. Die tatsächliche Klickposition ist hier die verlässliche Quelle.
      const clickedControl = event.target.closest('button, quantity-input, modal-opener');
      const mediaRect = media?.getBoundingClientRect();
      const clickedImage = mediaRect
        && event.clientX >= mediaRect.left
        && event.clientX <= mediaRect.right
        && event.clientY >= mediaRect.top
        && event.clientY <= mediaRect.bottom;
      if (card && clickedImage && !clickedControl) {
        const input = card.querySelector('[data-fence-config-item]');
        if (input && !input.disabled) {
          event.preventDefault();
          event.stopPropagation();
          const current = Math.max(0, parseInt(input.value, 10) || 0);
          const max = input.max === '' ? Infinity : Number(input.max);
          input.value = Math.min(current + 1, Number.isFinite(max) ? max : current + 1);
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    };
    document.addEventListener('change', this.onChange);
    document.addEventListener('input', this.onChange);
    document.addEventListener('click', this.onQuantityClick);
    this.accessoryButtons.forEach((button) => button.addEventListener('click', () => this.addMissing(button.dataset.addRequired)));
    this.wpcColorButtons.forEach((button) => button.addEventListener('click', () => this.selectWpcColor(button.dataset.wpcColor)));
    this.wpcPanelColorButtons.forEach((button) => button.addEventListener('click', () => this.selectWpcPanelColor(button.dataset.wpcPanelColor)));
    this.postLengthSelect?.addEventListener('change', () => this.selectPostLength(this.postLengthSelect.value));
    this.wirePostTypeButtons.forEach((button) => (
      button.addEventListener('click', () => this.selectWirePostType(button.dataset.wirePostType))
    ));
    this.cartButton?.addEventListener('click', () => this.addAllToCart());
    this.scene.addEventListener('click', (event) => {
      const button = event.target.closest('[data-remove-fence-item]');
      if (button) this.removeElement(button.dataset.removeFenceItem);
    });
    this.dragSort = new FenceSceneDragSort({
      scene: this.scene,
      getOrder: () => this.elementOrder,
      setOrder: (order) => {
        this.elementOrder = order;
      },
      onReorder: (focusKey) => {
        this.render();
        this.rebalancePosts();
        this.saveState();
        if (focusKey) this.scene.querySelector(`[data-fence-key="${CSS.escape(focusKey)}"]`)?.focus();
      },
    });
    this.hidden = false;
    this.render();
    this.restoredInputs?.forEach((input) => input.dispatchEvent(new Event('input', { bubbles: true })));
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onChange);
    document.removeEventListener('input', this.onChange);
    document.removeEventListener('click', this.onQuantityClick);
  }

  number(value) {
    const match = String(value || '').replace(',', '.').match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : 0;
  }

  restoreState() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
      if (!stored || typeof stored !== 'object') return;
      const quantities = stored.quantities || {};
      this.restoredInputs = [];
      document.querySelectorAll('[data-fence-config-item][data-variant-id]').forEach((input) => {
        const storedQuantity = Number(quantities[input.dataset.variantId]);
        if (!Number.isInteger(storedQuantity) || storedQuantity < 0) return;
        const max = input.max === '' ? Infinity : Number(input.max);
        input.value = Math.min(storedQuantity, Number.isFinite(max) ? max : storedQuantity);
        this.restoredInputs.push(input);
      });
      if (Array.isArray(stored.elementOrder)) {
        this.elementOrder = stored.elementOrder.filter((key) => typeof key === 'string');
      }
      if (WPC_FRAME_COLOR_PATTERNS.some(([color]) => color === stored.wpcFrameColor)) {
        this.wpcFrameColor = stored.wpcFrameColor;
      }
      if (WPC_PANEL_COLOR_PATTERNS.some(([color]) => color === stored.wpcPanelColor)) {
        this.wpcPanelColor = stored.wpcPanelColor;
      }
      if ([1800, 2400, 2700].includes(Number(stored.postLengthMm))) {
        this.postLengthMm = Number(stored.postLengthMm);
      }
      if (['tom', 'jerry'].includes(stored.wirePostType)) {
        this.wirePostType = stored.wirePostType;
      }
    } catch (error) {
      console.warn('Zaunkonfiguration konnte nicht wiederhergestellt werden.', error);
    }
  }

  saveState() {
    try {
      const quantities = {};
      document.querySelectorAll('[data-fence-config-item][data-variant-id]').forEach((input) => {
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        if (quantity > 0) quantities[input.dataset.variantId] = quantity;
      });
      window.localStorage.setItem(this.storageKey, JSON.stringify({
        quantities,
        elementOrder: this.elementOrder,
        wpcFrameColor: this.wpcFrameColor || 'silber',
        wpcPanelColor: this.wpcPanelColor || '',
        wirePostType: this.wirePostType || 'tom',
        postLengthMm: this.postLengthMm || 2400,
      }));
    } catch (error) {
      console.warn('Zaunkonfiguration konnte nicht gespeichert werden.', error);
    }
  }

  product(input) {
    const title = input.dataset.productTitle || '';
    const dimensions = parseFenceDimensions(title);
    const elementType = (input.dataset.elementType || '').toLowerCase();
    const productFallback = `${input.dataset.productType || ''} ${title}`.toLowerCase();
    const accessoryMetafield = (input.dataset.accessoryType || '').toLowerCase();
    // Allgemeine Metafeldwerte wie "Zubehör" dürfen die konkrete Bezeichnung
    // "Start & Abschluss Set" aus dem Produkttitel nicht verdecken.
    const accessorySource = `${accessoryMetafield} ${elementType} ${productFallback}`.trim();
    // Alle Quellen gemeinsam auswerten: Ein allgemeines Form-Metafeld (z. B. "Rankgitter")
    // darf präzisere Angaben wie "Doppelbogen-Abschluß" im Produkttitel nicht verdecken.
    const shapeSource = `${input.dataset.elementShape || ''} ${elementType} ${productFallback}`.toLowerCase();
    const visualStyles = detectVisualStyles(shapeSource);
    const isHAnchor = /h[\s-]*(?:anker|pfostenträger|pfostentraeger)/.test(accessorySource);
    const isPostCarrier = /pfostenträger|pfostentraeger/.test(accessorySource);
    const isThroughBolt = /durchgangs[\s-]*schraub/.test(accessorySource);
    const hasPostMetafield = input.dataset.isFencePost === 'true' || input.dataset.isFencePost === 'false';
    const isPostFromMetafield = input.dataset.isFencePost === 'true';
    const isPost = !isPostCarrier
      && !isThroughBolt
      && (hasPostMetafield ? isPostFromMetafield : /pfosten|zaunpfahl|(?:^|\s)pfahl(?:\s|$)/.test(accessorySource));
    let accessoryType = '';
    if (isHAnchor) accessoryType = 'h-anchor';
    else if (isPostCarrier) accessoryType = 'post-carrier';
    else if (isThroughBolt) accessoryType = 'through-bolt';
    else if (isPost) accessoryType = 'post';
    else if (/start\s*(?:&|und)\s*abschlu(?:ss|ß)\s*set/.test(accessorySource)) accessoryType = 'start-end';
    else if (/winkelbeschlag|winkelbeschläge|winkelbeschlaege/.test(accessorySource)) accessoryType = 'bracket';
    else if (
      accessoryMetafield
      || /(?:^|\s)zubehör(?:\s|$)|(?:^|\s)zubehoer(?:\s|$)|einschlaghilfe|montagehilfe|zaunhalter|pflegeöl|pflegeoel|insektenhaus|futterstelle|pflanzkasten|blumenkasten|vogelhaus|(?:^|\s)regal(?:\s|$)|(?:^|\s)haken(?:\s|$)|konstruktionsholz/.test(accessorySource)
    ) accessoryType = 'other';
    const width = this.number(input.dataset.productWidth) || dimensions.width;
    let height = this.number(input.dataset.productHeight) || dimensions.height;
    let lowHeight = this.number(input.dataset.lowHeight) || dimensions.lowHeight || height * 0.55;
    let shape = detectShape(
      shapeSource,
      Boolean(input.dataset.lowHeight || dimensions.lowHeight),
      this.isWoodenGardenFenceType,
    );
    if (isArchConvexShape(shape) && lowHeight > height) [height, lowHeight] = [lowHeight, height];
    // Steckzaun-Kollektionen enthalten neben WPC auch Aluminium-Systeme (z. B.
    // SELLIN). Sie verwenden dieselbe Lamellen-Darstellung und Zubehörlogik.
    // Ohne diese Einordnung fällt ein "Alu-Steckzaun-Set" auf das hölzerne
    // Standardelement zurück.
    if (this.isInsertFenceSystemType && !accessoryType) {
      if (shape === 'gate') shape = 'wpc-gate';
      else if (shape === 'standard') shape = 'wpc-panel';
    }
    const colorPair = title.toLowerCase().match(/(smoke\s*white|charcoal|anthrazit|antique|beige|mokka|teak|grau|weiß|weiss|white)\s*\/\s*(anthrazit|silber|silver)/);
    const explicitFillText = title.toLowerCase().match(/füllung\s+(.+?)\s+rahmen\b/)?.[1] || '';
    const explicitFrameText = title.toLowerCase().match(/rahmen\s+([^,]+)/)?.[1] || '';
    const panelColorSource = productFallback.split(/\brahmen\b/)[0];
    const panelColor = colorFromText(explicitFillText, WPC_PANEL_COLOR_PATTERNS)
      || colorFromText(colorPair?.[1], WPC_PANEL_COLOR_PATTERNS)
      || colorFromText(panelColorSource, WPC_PANEL_COLOR_PATTERNS);
    const explicitFrameColor = colorFromText(explicitFrameText, WPC_FRAME_COLOR_PATTERNS)
      || colorFromText(colorPair?.[2], WPC_FRAME_COLOR_PATTERNS);
    const insertSystemColor = colorFromText(productFallback, WPC_FRAME_COLOR_PATTERNS);
    const isAluminumInsertPanel = shape === 'wpc-panel' && /alu(?:minium)?[\s-]*steckzaun/.test(productFallback);
    const frameColor = explicitFrameColor
      || (isAluminumInsertPanel ? insertSystemColor : '')
      || (shape === 'wpc-panel' ? 'silber' : '');
    const systemColor = explicitFrameColor
      || insertSystemColor
      || frameColor;
    const isWpcField = shape === 'wpc-gate' || (shape === 'wpc-panel' && /steckzaun[\s-]*set/.test(productFallback));
    const isWpcLamella = shape === 'wpc-panel' && /wpc[\s-]*lamelle/.test(productFallback);
    const isWireMesh = visualStyles.includes('wire-mesh');
    const isInsertFenceSystem = this.isInsertFenceSystemType
      || /steckzaun|wollbach/.test(`${elementType} ${productFallback}`);
    const isLightline = /lightline/.test(`${elementType} ${productFallback}`);
    const bracketsIncluded = /lightline[\s-]*ks|(?:^|[\s-])eutin(?:[\s-]|$)|inkl\.?\s*4\s*alu[\s-]*zaunhalter/.test(`${elementType} ${productFallback}`);
    const postsIncluded = shape === 'gate' && /inkl\.?\s*pfosten/.test(productFallback);
    const wirePostType = isPost
      ? (/\btom\b/.test(productFallback) ? 'tom' : /\bjerry\b/.test(productFallback) ? 'jerry' : '')
      : '';
    const isGatePost = isPost && /tor[\s-]*pfosten/.test(productFallback);
    const millimeterDimensions = title.match(/(\d+(?:[.,]\d+)?)\s*x\s*(\d+(?:[.,]\d+)?)\s*x\s*(\d+(?:[.,]\d+)?)\s*mm/i);
    const postLengthMm = isPost
      ? this.number(millimeterDimensions?.[3]) || (height >= 1000 ? height : height * 10)
      : 0;
    const defaultPackageSize = accessoryType === 'bracket' ? 4 : accessoryType === 'through-bolt' ? 2 : 1;
    const packageSize = this.number(input.dataset.packageSize) || defaultPackageSize;
    const defaultRequiredPerElement = accessoryType === 'bracket' ? 4 : accessoryType === 'through-bolt' ? 2 : 1;
    const requiredPerElement = this.number(input.dataset.requiredPerElement) || defaultRequiredPerElement;
    const price = this.number(input.dataset.productPrice);
    const variantId = input.dataset.variantId || '';
    const configKeyBase = variantId ? `variant-${variantId}` : (input.id || title);
    return { input, deliveryTime: input.dataset.deliveryTime, title, width, height, lowHeight, shape, visualStyles, panelColor, frameColor, systemColor, isWpcField, isWpcLamella, isWireMesh, isInsertFenceSystem, isLightline, bracketsIncluded, postsIncluded, wirePostType, postLengthMm, isPost, isGatePost, accessoryType, packageSize, requiredPerElement, price, variantId, configKeyBase, quantity: Math.max(0, parseInt(input.value, 10) || 0) };
  }

  addMissing(type) {
    const stateKey = type === 'start-end' ? 'startEnd' : type;
    const state = this.accessoryState?.[stateKey];
    const fills = state?.fills || [];
    if (!fills.length) return;
    const updatedInputs = [];
    if ((type === 'post' || type === 'start-end') && state?.eligibleProducts) {
      state.products
        .filter((product) => !state.eligibleProducts.includes(product) && product.quantity > 0)
        .forEach((product) => {
          product.input.value = 0;
          updatedInputs.push(product.input);
        });
    }
    if (type === 'post' && state?.carrier?.eligibleProducts) {
      state.carrier.products
        .filter((product) => !state.carrier.eligibleProducts.includes(product) && product.quantity > 0)
        .forEach((product) => {
          product.input.value = 0;
          updatedInputs.push(product.input);
        });
    }
    fills.forEach(({ product, packages }) => {
      const input = product.input;
      if (!input || packages <= 0) return;
      input.value = (parseInt(input.value, 10) || 0) + packages;
      updatedInputs.push(input);
    });

    // Alle Mengen zuerst gemeinsam setzen. So sieht jede nachgelagerte Berechnung
    // bereits den vollständigen Korrekturstand und keinen unvollständigen Zwischenstand.
    this.render();
    updatedInputs.forEach((input) => {
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // Edge heights of an already-aligned element (i.e. after alignSlopes has picked slopeHighSide).
  edgeHeights(item) {
    return edgesForShape(item, item.slopeHighSide);
  }

  buildPostState(products, postPositions, requiredSystemColor = '', requiredPostLengthMm = 0, requiredWirePostType = '', gatePostsOnly = false) {
    const accessoryProducts = products.filter((item) => (
      item.accessoryType === 'post' && item.isGatePost === gatePostsOnly
    ));
    const typeEligibleProducts = requiredWirePostType
      ? accessoryProducts.filter((item) => item.wirePostType === requiredWirePostType)
      : accessoryProducts;
    const colorEligibleProducts = requiredSystemColor
      ? typeEligibleProducts.filter((item) => !item.systemColor || item.systemColor === requiredSystemColor)
      : typeEligibleProducts;
    const eligibleProducts = requiredPostLengthMm
      ? colorEligibleProducts.filter((item) => item.postLengthMm === requiredPostLengthMm)
      : colorEligibleProducts;
    const usablePostHeight = (item) => item.postLengthMm ? item.postLengthMm / 10 : item.height || Infinity;
    // Jedes gewählte Pfosten-Stück mit seiner Länge (ohne Längenangabe = universell einsetzbar)
    const pieces = eligibleProducts
      .flatMap((item) => Array.from({ length: item.quantity * item.packageSize }, () => usablePostHeight(item)))
      .sort((a, b) => b - a);
    const requirements = postPositions
      .map((length, position) => ({ length, position }))
      .sort((a, b) => b.length - a.length);
    const assignedPositions = new Set();
    const missingByLength = new Map();
    let pieceIndex = 0;
    requirements.forEach((requirement) => {
      if (pieceIndex < pieces.length && pieces[pieceIndex] >= requirement.length) {
        assignedPositions.add(requirement.position);
        pieceIndex += 1;
      } else {
        missingByLength.set(requirement.length, (missingByLength.get(requirement.length) || 0) + 1);
      }
    });
    const fillPieces = new Map();
    let unfillablePieces = 0;
    missingByLength.forEach((count, length) => {
      const fillProduct = eligibleProducts
        .filter((item) => !item.input.disabled && usablePostHeight(item) >= length)
        .sort((a, b) => usablePostHeight(a) - usablePostHeight(b))[0];
      if (fillProduct) fillPieces.set(fillProduct, (fillPieces.get(fillProduct) || 0) + count);
      else unfillablePieces += count;
    });
    const fills = [...fillPieces].map(([product, missingPieces]) => ({
      product,
      packages: Math.ceil(missingPieces / product.packageSize),
    }));
    return {
      products: accessoryProducts,
      eligibleProducts,
      requiredPostLengthMm,
      requiredPieces: postPositions.length,
      selectedPieces: pieces.length,
      assignedPositions,
      missingByLength,
      fills,
      unfillablePieces,
      missing: fills.reduce((sum, fill) => sum + fill.packages, 0),
    };
  }

  buildBracketState(products, elementCount) {
    const accessoryProducts = products.filter((item) => item.accessoryType === 'bracket');
    const fillProduct = accessoryProducts.find((item) => !item.input.disabled);
    const requirementFactor = accessoryProducts[0]?.requiredPerElement || 4;
    const requiredPieces = elementCount * requirementFactor;
    const selectedPieces = accessoryProducts.reduce((sum, item) => sum + item.quantity * item.packageSize, 0);
    const missingPieces = Math.max(0, requiredPieces - selectedPieces);
    const missing = fillProduct ? Math.ceil(missingPieces / fillProduct.packageSize) : 0;
    return {
      products: accessoryProducts,
      requiredPieces,
      selectedPieces,
      missingByLength: new Map(),
      fills: fillProduct && missing > 0 ? [{ product: fillProduct, packages: missing }] : [],
      unfillablePieces: fillProduct ? 0 : missingPieces,
      missing,
    };
  }

  rememberBaseAvailability(input) {
    if (!input) return;
    if (input.dataset.fenceBaseDisabled === undefined) {
      input.dataset.fenceBaseDisabled = String(input.disabled);
    }
    input.closest('quantity-input')?.querySelectorAll('button').forEach((button) => {
      if (button.dataset.fenceBaseDisabled === undefined) {
        button.dataset.fenceBaseDisabled = String(button.disabled);
      }
    });
  }

  isBaseDisabled(input) {
    this.rememberBaseAvailability(input);
    return input?.dataset.fenceBaseDisabled === 'true';
  }

  setWpcProductDisabled(product, colorDisabled) {
    const input = product.input;
    this.rememberBaseAvailability(input);
    const quantityControl = input.closest('quantity-input');
    const productContainer = input.closest('.product-card-wrapper, .accessory-item');
    const productTile = input.closest('.grid__item, .accessory-item') || productContainer;
    input.disabled = this.isBaseDisabled(input) || colorDisabled;
    input.dataset.fenceColorDisabled = String(colorDisabled);
    quantityControl?.querySelectorAll('button').forEach((button) => {
      button.disabled = button.dataset.fenceBaseDisabled === 'true' || colorDisabled;
    });
    if (quantityControl) {
      if (colorDisabled) {
        quantityControl.setAttribute('aria-disabled', 'true');
        quantityControl.title = 'Nicht mit den gewählten WOLLBACH-Farben kombinierbar';
      } else {
        quantityControl.removeAttribute('aria-disabled');
        quantityControl.removeAttribute('title');
      }
    }
    productTile?.classList.toggle('is-fence-color-disabled', colorDisabled);
  }

  // Das erste gewählte WOLLBACH-Feld bzw. Tor legt die Feldfarbe fest.
  // Andere Feldfarben werden geleert und bis zum Entfernen der letzten Auswahl gesperrt.
  syncWpcPanelColor(sourceInput = null) {
    const products = [...document.querySelectorAll('[data-fence-config-item]')].map((input) => this.product(input));
    const fields = products.filter((product) => (product.isWpcField || product.isWireMesh) && product.panelColor);
    const lockableProducts = products.filter((product) => (
      product.isWpcField || product.isWpcLamella || product.isWireMesh
    ) && product.panelColor);
    const source = sourceInput ? fields.find((product) => product.input === sourceInput) : null;
    const selectedFields = fields.filter((product) => product.quantity > 0);
    const selectedInCurrentColor = selectedFields.find((product) => product.panelColor === this.wpcPanelColor);
    const activeColor = source?.quantity > 0
      ? source.panelColor
      : (selectedInCurrentColor || selectedFields[0])?.panelColor || '';
    this.wpcPanelColor = activeColor;
    const selectedGate = source?.quantity > 0 && source.shape === 'wpc-gate'
      ? source
      : selectedFields.find((product) => product.shape === 'wpc-gate');
    if (selectedGate?.frameColor) this.wpcFrameColor = selectedGate.frameColor;

    const changedInputs = [];
    lockableProducts.forEach((product) => {
      const panelColorDisabled = Boolean(activeColor && product.panelColor !== activeColor);
      const frameColorDisabled = Boolean(
        activeColor
        && product.shape === 'wpc-gate'
        && product.frameColor
        && product.frameColor !== this.wpcFrameColor,
      );
      const colorDisabled = panelColorDisabled || frameColorDisabled;
      if (colorDisabled && product.quantity > 0) {
        product.input.value = 0;
        changedInputs.push(product.input);
      }
      this.setWpcProductDisabled(product, colorDisabled);
    });
    return changedInputs;
  }

  dispatchQuantityChanges(inputs = []) {
    const changedInputs = [...new Set(inputs.filter(Boolean))];
    if (!changedInputs.length) return;
    this.isSynchronizingWpc = true;
    try {
      changedInputs.forEach((input) => {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } finally {
      this.isSynchronizingWpc = false;
    }
  }

  swapSelectedWpcGates(frameColor) {
    const products = [...document.querySelectorAll('[data-fence-config-item]')].map((input) => this.product(input));
    const gates = products.filter((product) => product.shape === 'wpc-gate');
    const changedInputs = [];
    gates.filter((gate) => gate.quantity > 0 && gate.frameColor !== frameColor).forEach((gate) => {
      const replacement = gates
        .filter((candidate) => candidate !== gate
          && candidate.panelColor === gate.panelColor
          && candidate.frameColor === frameColor
          && !this.isBaseDisabled(candidate.input))
        .sort((a, b) => {
          const aDistance = Math.abs((a.width || 0) - (gate.width || 0)) + Math.abs((a.height || 0) - (gate.height || 0));
          const bDistance = Math.abs((b.width || 0) - (gate.width || 0)) + Math.abs((b.height || 0) - (gate.height || 0));
          return aDistance - bDistance;
        })[0];
      if (!replacement) return;

      const replacementQuantity = Math.max(0, parseInt(replacement.input.value, 10) || 0);
      const maxQuantity = replacement.input.max === '' ? Infinity : Number(replacement.input.max);
      const availableCapacity = Number.isFinite(maxQuantity) ? Math.max(0, maxQuantity - replacementQuantity) : gate.quantity;
      const transferQuantity = Math.min(gate.quantity, availableCapacity);
      if (!transferQuantity) return;

      const remainingQuantity = gate.quantity - transferQuantity;
      gate.input.value = remainingQuantity;
      replacement.input.value = replacementQuantity + transferQuantity;
      for (let index = 0; index < transferQuantity; index += 1) {
        const oldKey = `${gate.configKeyBase}-${remainingQuantity + index}`;
        const newKey = `${replacement.configKeyBase}-${replacementQuantity + index}`;
        this.elementOrder = this.elementOrder.map((key) => (key === oldKey ? newKey : key));
      }
      changedInputs.push(gate.input, replacement.input);
    });
    return changedInputs;
  }

  swapSelectedWpcPanelProducts(panelColor) {
    const products = [...document.querySelectorAll('[data-fence-config-item]')].map((input) => this.product(input));
    const colorProducts = products.filter((product) => (
      product.isWpcField || product.isWpcLamella || product.isWireMesh
    ) && product.panelColor);
    const selectedProducts = colorProducts.filter((product) => product.quantity > 0 && product.panelColor !== panelColor);
    const plannedQuantities = new Map();
    const plans = selectedProducts.map((product) => {
      const sameKind = (candidate) => {
        if (product.isWireMesh) return candidate.isWireMesh;
        if (product.isWpcLamella) return candidate.isWpcLamella;
        if (product.shape === 'wpc-gate') {
          return candidate.shape === 'wpc-gate' && candidate.frameColor === product.frameColor;
        }
        return candidate.isWpcField && candidate.shape === 'wpc-panel';
      };
      const replacement = colorProducts
        .filter((candidate) => candidate !== product
          && candidate.panelColor === panelColor
          && sameKind(candidate)
          && !this.isBaseDisabled(candidate.input))
        .sort((a, b) => {
          const aDistance = Math.abs((a.width || 0) - (product.width || 0)) + Math.abs((a.height || 0) - (product.height || 0));
          const bDistance = Math.abs((b.width || 0) - (product.width || 0)) + Math.abs((b.height || 0) - (product.height || 0));
          return aDistance - bDistance;
        })[0];
      if (!replacement) return null;
      const replacementQuantity = plannedQuantities.has(replacement)
        ? plannedQuantities.get(replacement)
        : Math.max(0, parseInt(replacement.input.value, 10) || 0);
      const maxQuantity = replacement.input.max === '' ? Infinity : Number(replacement.input.max);
      if (Number.isFinite(maxQuantity) && replacementQuantity + product.quantity > maxQuantity) return null;
      plannedQuantities.set(replacement, replacementQuantity + product.quantity);
      return { product, replacement };
    });
    if (plans.some((plan) => !plan)) return null;

    const changedInputs = [];
    plans.forEach(({ product, replacement }) => {
      const replacementQuantity = Math.max(0, parseInt(replacement.input.value, 10) || 0);
      product.input.value = 0;
      replacement.input.value = replacementQuantity + product.quantity;
      for (let index = 0; index < product.quantity; index += 1) {
        const oldKey = `${product.configKeyBase}-${index}`;
        const newKey = `${replacement.configKeyBase}-${replacementQuantity + index}`;
        this.elementOrder = this.elementOrder.map((key) => (key === oldKey ? newKey : key));
      }
      changedInputs.push(product.input, replacement.input);
    });
    return changedInputs;
  }

  selectWpcColor(color) {
    if (!this.availableWpcFrameColors?.has(color)) return;
    this.wpcFrameColor = color;
    const changedInputs = this.swapSelectedWpcGates(color);
    changedInputs.push(...this.syncWpcPanelColor());
    this.dispatchQuantityChanges(changedInputs);
    this.render();
    this.rebalancePosts(true);
    this.trimAccessoryToRequired(this.accessoryState?.startEnd);
    this.trimAccessoryToRequired(this.accessoryState?.postCarrier);
    this.render();
    this.saveState();
  }

  selectWpcPanelColor(color) {
    if (!this.availableWpcPanelColors?.has(color)) return;
    const changedInputs = this.swapSelectedWpcPanelProducts(color);
    if (changedInputs === null) return;
    this.wpcPanelColor = color;
    changedInputs.push(...this.syncWpcPanelColor());
    this.dispatchQuantityChanges(changedInputs);
    this.render();
    if (this.isWireMeshConfiguration) {
      this.rebalancePosts(true);
      this.render();
    }
    this.saveState();
  }

  selectPostLength(value) {
    const postLengthMm = Number(value);
    if (![1800, 2400, 2700].includes(postLengthMm)) return;
    this.postLengthMm = postLengthMm;
    this.render();
    this.rebalancePosts(true);
    this.trimAccessoryToRequired(this.accessoryState?.postCarrier);
    this.render();
    this.saveState();
  }

  selectWirePostType(type) {
    if (!this.availableWirePostTypes?.has(type)) return;
    this.wirePostType = type;
    this.render();
    this.rebalancePosts(true);
    this.render();
    this.saveState();
  }

  buildStartEndState(products, elements, requiredSystemColor = '') {
    const accessoryProducts = products.filter((item) => item.accessoryType === 'start-end');
    let runCount = 0;
    let insideRun = false;
    elements.forEach((item) => {
      const isWpcPanel = item.shape === 'wpc-panel';
      if (isWpcPanel && !insideRun) runCount += 1;
      insideRun = isWpcPanel;
    });

    const preferredColor = requiredSystemColor
      || elements.find((item) => item.shape === 'wpc-gate' && item.frameColor)?.frameColor
      || elements.find((item) => item.shape === 'wpc-panel')?.frameColor
      || 'silber';
    const matchingProducts = accessoryProducts.filter((item) => !item.systemColor || item.systemColor === preferredColor);
    const selectedPieces = matchingProducts.reduce((sum, item) => sum + item.quantity * item.packageSize, 0);
    const missingPieces = Math.max(0, runCount - selectedPieces);
    const fillProduct = matchingProducts.find((item) => !item.input.disabled);
    const missing = fillProduct ? Math.ceil(missingPieces / fillProduct.packageSize) : 0;
    return {
      products: accessoryProducts,
      eligibleProducts: matchingProducts,
      requiredPieces: runCount,
      selectedPieces,
      preferredColor,
      fills: fillProduct && missing ? [{ product: fillProduct, packages: missing }] : [],
      unfillablePieces: fillProduct ? 0 : missingPieces,
      missing,
    };
  }

  buildHardwareState(products, type, requiredPieces) {
    const accessoryProducts = products.filter((item) => item.accessoryType === type);
    const fillProduct = accessoryProducts.find((item) => !item.input.disabled);
    const selectedPieces = accessoryProducts.reduce((sum, item) => sum + item.quantity * item.packageSize, 0);
    const missingPieces = Math.max(0, requiredPieces - selectedPieces);
    const missing = fillProduct ? Math.ceil(missingPieces / fillProduct.packageSize) : 0;
    return {
      products: accessoryProducts,
      requiredPieces,
      selectedPieces,
      fills: fillProduct && missing ? [{ product: fillProduct, packages: missing }] : [],
      unfillablePieces: fillProduct ? 0 : missingPieces,
      missing,
    };
  }

  buildPostCarrierState(products, requiredPieces) {
    const accessoryProducts = products.filter((item) => item.accessoryType === 'post-carrier');
    const availableProducts = accessoryProducts.filter((item) => !item.input.disabled);
    const state = this.buildHardwareState(
      accessoryProducts,
      'post-carrier',
      availableProducts.length ? requiredPieces : 0,
    );
    return { ...state, products: accessoryProducts, eligibleProducts: accessoryProducts };
  }

  buildFoundationState(products, postCount) {
    const anchorAvailable = products.some((item) => (
      item.accessoryType === 'h-anchor' && !item.input.disabled
    ));
    const requiredPostCount = anchorAvailable ? postCount : 0;
    const boltProduct = products.find((item) => item.accessoryType === 'through-bolt');
    const boltsPerPost = boltProduct?.requiredPerElement || 2;
    const anchors = this.buildHardwareState(products, 'h-anchor', requiredPostCount);
    const bolts = this.buildHardwareState(products, 'through-bolt', requiredPostCount * boltsPerPost);
    return {
      anchors,
      bolts,
      products: [...anchors.products, ...bolts.products],
      fills: [...anchors.fills, ...bolts.fills],
      missing: anchors.missing + bolts.missing,
      unfillablePieces: anchors.unfillablePieces + bolts.unfillablePieces,
    };
  }

  async addAllToCart() {
    const missingPosts = this.accessoryState?.post
      ? [...this.accessoryState.post.missingByLength.values()].reduce((sum, count) => sum + count, 0)
      : 0;
    const missingBrackets = this.accessoryState?.bracket
      ? Math.max(0, this.accessoryState.bracket.requiredPieces - this.accessoryState.bracket.selectedPieces)
      : 0;
    const missingStartEnd = this.accessoryState?.startEnd
      ? Math.max(0, this.accessoryState.startEnd.requiredPieces - this.accessoryState.startEnd.selectedPieces)
      : 0;
    const missingPostCarriers = this.accessoryState?.postCarrier
      ? Math.max(0, this.accessoryState.postCarrier.requiredPieces - this.accessoryState.postCarrier.selectedPieces)
      : 0;
    const missingAnchors = this.accessoryState?.foundation
      ? Math.max(0, this.accessoryState.foundation.anchors.requiredPieces - this.accessoryState.foundation.anchors.selectedPieces)
      : 0;
    const missingBolts = this.accessoryState?.foundation
      ? Math.max(0, this.accessoryState.foundation.bolts.requiredPieces - this.accessoryState.foundation.bolts.selectedPieces)
      : 0;

    if (missingPosts > 0 || missingBrackets > 0 || missingStartEnd > 0 || missingPostCarriers > 0 || missingAnchors > 0 || missingBolts > 0) {
      const missingItems = [];
      if (missingPosts > 0) missingItems.push(`${missingPosts} Pfosten`);
      if (missingBrackets > 0) missingItems.push(`${missingBrackets} Winkelbeschläge`);
      if (missingStartEnd > 0) missingItems.push(`${missingStartEnd} Start- & Abschluss-Sets`);
      if (missingPostCarriers > 0) missingItems.push(`${missingPostCarriers} Pfostenträger`);
      if (missingAnchors > 0) missingItems.push(`${missingAnchors} H-Pfostenträger`);
      if (missingBolts > 0) missingItems.push(`${missingBolts} Durchgangsschrauben`);
      const confirmed = await this.confirmIncompleteConfiguration(missingItems);
      if (!confirmed) {
        this.cartStatus.textContent = 'Nicht zum Warenkorb hinzugefügt – bitte ergänzen Sie die fehlenden Zusatzprodukte.';
        return;
      }
    }

    const quantities = new Map();
    document.querySelectorAll('[data-fence-config-item][data-variant-id]').forEach((input) => {
      const quantity = Math.max(0, parseInt(input.value, 10) || 0);
      if (!quantity || input.disabled) return;
      const id = input.dataset.variantId;
      quantities.set(id, (quantities.get(id) || 0) + quantity);
    });
    const items = [...quantities].map(([id, quantity]) => ({ id: Number(id), quantity }));
    if (!items.length || this.cartButton.disabled) return;

    const cartDrawer = document.querySelector('cart-drawer');
    const body = { items };
    if (cartDrawer) {
      body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      cartDrawer.setActiveElement(this.cartButton);
    }
    this.cartButton.disabled = true;
    this.cartButton.classList.add('loading');
    this.cartButton.querySelector('.loading__spinner')?.classList.remove('hidden');
    this.cartStatus.textContent = '';

    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Warenkorb konnte nicht aktualisiert werden.');
      this.cartStatus.textContent = 'Die Konfiguration wurde zum Warenkorb hinzugefügt.';
      if (cartDrawer && result.sections) {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(result);
      } else {
        window.location.assign(window.routes?.cart_url || '/cart');
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fence-configurator', cartData: result });
      }
    } catch (error) {
      console.error(error);
      this.cartStatus.textContent = error.message || 'Beim Hinzufügen ist ein Fehler aufgetreten.';
    } finally {
      this.cartButton.classList.remove('loading');
      this.cartButton.querySelector('.loading__spinner')?.classList.add('hidden');
      this.updateCartButton();
    }
  }

  confirmIncompleteConfiguration(missingItems) {
    const dialog = this.querySelector('[data-incomplete-dialog]');
    if (!dialog) return Promise.resolve(false);
    const message = dialog.querySelector('[data-incomplete-message]');
    const cancelButton = dialog.querySelector('[data-incomplete-cancel]');
    const confirmButton = dialog.querySelector('[data-incomplete-confirm]');
    if (message) message.textContent = `Es fehlen: ${missingItems.join(', ')}.`;

    return new Promise((resolve) => {
      const finish = (confirmed) => {
        cancelButton?.removeEventListener('click', onCancel);
        confirmButton?.removeEventListener('click', onConfirm);
        dialog.removeEventListener('cancel', onDialogCancel);
        if (dialog.open) dialog.close();
        resolve(confirmed);
      };
      const onCancel = () => finish(false);
      const onConfirm = () => finish(true);
      const onDialogCancel = (event) => {
        event.preventDefault();
        finish(false);
      };
      cancelButton?.addEventListener('click', onCancel);
      confirmButton?.addEventListener('click', onConfirm);
      dialog.addEventListener('cancel', onDialogCancel);
      dialog.showModal();
    });
  }

  updateCartButton() {
    if (!this.cartButton) return;
    const hasSelection = [...document.querySelectorAll('[data-fence-config-item][data-variant-id]')]
      .some((input) => !input.disabled && (parseInt(input.value, 10) || 0) > 0);
    this.cartButton.disabled = !hasSelection;
  }

  removeElement(key) {
    const item = this.renderedItems?.get(key);
    if (!item?.input) return;
    const wasExactlyComplete = {
      post: [...this.accessoryState.post.missingByLength.values()].reduce((sum, count) => sum + count, 0) === 0
        && this.accessoryState.post.selectedPieces === this.accessoryState.post.requiredPieces,
      bracket: this.accessoryState.bracket.selectedPieces === this.accessoryState.bracket.requiredPieces,
      startEnd: this.accessoryState.startEnd.selectedPieces === this.accessoryState.startEnd.requiredPieces,
      postCarrier: this.accessoryState.postCarrier.selectedPieces === this.accessoryState.postCarrier.requiredPieces,
      anchor: this.accessoryState.foundation.anchors.selectedPieces === this.accessoryState.foundation.anchors.requiredPieces,
      bolt: this.accessoryState.foundation.bolts.selectedPieces === this.accessoryState.foundation.bolts.requiredPieces,
    };
    const quantity = Math.max(0, (parseInt(item.input.value, 10) || 0) - 1);
    const prefix = `${item.configKeyBase}-`;
    const removedIndex = Number(key.slice(prefix.length));
    this.elementOrder = this.elementOrder
      .filter((itemKey) => itemKey !== key)
      .map((itemKey) => {
        if (!itemKey.startsWith(prefix)) return itemKey;
        const instanceIndex = Number(itemKey.slice(prefix.length));
        return instanceIndex > removedIndex ? `${prefix}${instanceIndex - 1}` : itemKey;
      });
    item.input.value = quantity;
    item.input.dispatchEvent(new Event('change', { bubbles: true }));
    if (wasExactlyComplete.post) this.rebalancePosts(true);
    const statesToSync = [];
    if (wasExactlyComplete.bracket) statesToSync.push(this.accessoryState.bracket);
    if (wasExactlyComplete.startEnd) statesToSync.push(this.accessoryState.startEnd);
    if (wasExactlyComplete.postCarrier) statesToSync.push(this.accessoryState.postCarrier);
    if (wasExactlyComplete.anchor) statesToSync.push(this.accessoryState.foundation.anchors);
    if (wasExactlyComplete.bolt) statesToSync.push(this.accessoryState.foundation.bolts);
    statesToSync.forEach((state) => this.trimAccessoryToRequired(state));
    this.render();
    this.saveState();
  }

  trimAccessoryToRequired(state) {
    if (!state?.products.length) return;
    const eligibleProducts = state.eligibleProducts || state.products;
    const preferredProduct = eligibleProducts.find((product) => product.quantity > 0 && !product.input.disabled)
      || eligibleProducts.find((product) => !product.input.disabled);
    const changedInputs = [];
    state.products.forEach((product) => {
      const targetQuantity = product === preferredProduct
        ? Math.ceil(state.requiredPieces / product.packageSize)
        : 0;
      if ((parseInt(product.input.value, 10) || 0) === targetQuantity) return;
      product.input.value = targetQuantity;
      changedInputs.push(product.input);
    });
    changedInputs.forEach((input) => input.dispatchEvent(new Event('change', { bubbles: true })));
  }

  // Nach dem Umsortieren ändern sich die benötigten Pfostenlängen pro Position:
  // Wenn zuvor exakt genug Pfosten gewählt waren, wird die Auswahl auf die
  // jetzt passenden Längen umgestellt (kürzester ausreichender Pfosten je Position).
  rebalancePosts(force = false) {
    const postState = this.accessoryState?.post;
    const positions = this.accessoryPostPositions || this.postPositions || [];
    if (!postState?.products.length) return;
    const eligibleProducts = postState.eligibleProducts || postState.products;
    if (!positions.length) {
      if (!force) return;
      const changedInputs = [];
      postState.products.forEach((item) => {
        if ((parseInt(item.input.value, 10) || 0) === 0) return;
        item.input.value = 0;
        changedInputs.push(item.input);
      });
      changedInputs.forEach((input) => input.dispatchEvent(new Event('change', { bubbles: true })));
      if (changedInputs.length) this.render();
      return;
    }
    if (!force && postState.selectedPieces !== postState.requiredPieces) return;
    const targets = new Map();
    let feasible = true;
    const usablePostHeight = (item) => item.postLengthMm ? item.postLengthMm / 10 : item.height || Infinity;
    const addTargets = (requiredPositions, candidates) => {
      requiredPositions.forEach((length) => {
        const product = candidates
          .filter((item) => !item.input.disabled && usablePostHeight(item) >= length)
          .sort((a, b) => usablePostHeight(a) - usablePostHeight(b))[0];
        if (!product) {
          feasible = false;
          return;
        }
        targets.set(product, (targets.get(product) || 0) + 1);
      });
    };
    addTargets(positions, postState.normalEligibleProducts || eligibleProducts);
    addTargets(this.gatePostPositions || [], postState.gateEligibleProducts || []);
    if (!feasible) return;
    let changed = false;
    postState.products.forEach((item) => {
      const pieces = targets.get(item) || 0;
      const quantity = Math.ceil(pieces / item.packageSize);
      if ((parseInt(item.input.value, 10) || 0) !== quantity) {
        item.input.value = quantity;
        item.input.dispatchEvent(new Event('input', { bubbles: true }));
        item.input.dispatchEvent(new Event('change', { bubbles: true }));
        changed = true;
      }
    });
    if (changed) this.render();
  }

  // Recomputes derived state from the current quantity inputs and the drag/keyboard order.
  computeState() {
    const products = [...document.querySelectorAll('[data-fence-config-item]')].map((input) => this.product(input));
    const elements = products.filter((item) => !item.accessoryType && item.width && item.height && item.quantity);
    const unorderedElements = elements.flatMap((item) => Array.from({ length: Math.min(item.quantity, MAX_RENDERED_ELEMENTS_PER_ITEM) }, (_, instanceIndex) => ({
      ...item,
      configKey: `${item.configKeyBase}-${instanceIndex}`,
    })));
    const availableKeys = unorderedElements.map((item) => item.configKey);
    this.elementOrder = this.elementOrder.filter((key) => availableKeys.includes(key));
    availableKeys.forEach((key) => {
      if (!this.elementOrder.includes(key)) this.elementOrder.push(key);
    });
    const elementsByKey = new Map(unorderedElements.map((item) => [item.configKey, item]));
    const expanded = this.elementOrder.map((key) => elementsByKey.get(key)).filter(Boolean);
    this.renderedItems = new Map(expanded.map((item) => [item.configKey, item]));
    this.alignSlopes(expanded);
    const hasWpcElements = expanded.some((item) => item.shape === 'wpc-panel' || item.shape === 'wpc-gate');
    this.isWpcConfiguration = hasWpcElements
      && expanded.every((item) => item.shape === 'wpc-panel' || item.shape === 'wpc-gate');
    this.isWireMeshConfiguration = expanded.length > 0
      && expanded.every((item) => item.isWireMesh);
    this.isGlassConfiguration = expanded.length > 0
      && expanded.every((item) => item.visualStyles.includes('glass'));
    this.isInsertFenceConfiguration = expanded.length > 0
      && expanded.every((item) => item.isInsertFenceSystem);
    this.isLightlineConfiguration = expanded.length > 0
      && expanded.every((item) => item.isLightline);
    this.availableWpcPanelColors = new Set(products
      .filter((item) => (item.isWpcField || item.isWireMesh) && item.panelColor && !this.isBaseDisabled(item.input))
      .map((item) => item.panelColor));
    this.availableWpcFrameColors = new Set(products
      .filter((item) => (item.isWpcField || item.accessoryType === 'post' || item.accessoryType === 'post-carrier' || item.accessoryType === 'start-end')
        && (item.frameColor || item.systemColor)
        && !this.isBaseDisabled(item.input))
      .map((item) => item.frameColor || item.systemColor));
    this.availableWirePostTypes = new Set(products
      .filter((item) => item.accessoryType === 'post' && item.wirePostType && !this.isBaseDisabled(item.input))
      .map((item) => item.wirePostType));
    if (this.availableWpcFrameColors.size && !this.availableWpcFrameColors.has(this.wpcFrameColor)) {
      [this.wpcFrameColor] = this.availableWpcFrameColors;
    }
    if (this.availableWirePostTypes.size && !this.availableWirePostTypes.has(this.wirePostType)) {
      [this.wirePostType] = this.availableWirePostTypes;
    }
    const requiredWpcColor = this.isWpcConfiguration ? this.wpcFrameColor : '';
    const requiredPostColor = this.isWireMeshConfiguration
      ? this.wpcPanelColor
      : (this.isGlassConfiguration ? this.wpcFrameColor : requiredWpcColor);
    const requiredPostLengthMm = this.isInsertFenceConfiguration ? this.postLengthMm : 0;

    // Benötigte Pfostenlänge pro Position = höchste angrenzende Elementkante
    const postPositions = expanded.length
      ? Array.from({ length: expanded.length + 1 }, (_, index) => {
          const leftEdge = expanded[index - 1] ? this.edgeHeights(expanded[index - 1])[1] : 0;
          const rightEdge = expanded[index] ? this.edgeHeights(expanded[index])[0] : 0;
          return Math.round(Math.max(leftEdge, rightEdge));
        })
      : [];
    this.postPositions = postPositions;
    // Bei Tor-Sets mit mitgelieferten Pfosten sind beide angrenzenden Positionen
    // bereits abgedeckt. Sie bleiben für Szene/Fundamente erhalten, lösen aber
    // keine zusätzlichen Zubehörpfosten aus.
    const accessoryPostPositions = postPositions.filter((_, index) => (
      !expanded[index - 1]?.postsIncluded && !expanded[index]?.postsIncluded
    ));
    this.accessoryPostPositions = accessoryPostPositions;
    // Stecksystem-Tore benötigen zusätzlich zu den normalen Pfostenpositionen
    // zwei speziell dafür vorgesehene Torpfosten.
    const gatePostPositions = this.isInsertFenceConfiguration
      ? expanded
        .filter((item) => item.shape === 'wpc-gate')
        .flatMap((item) => [item.height, item.height])
      : [];
    this.gatePostPositions = gatePostPositions;
    const requiredWirePostType = this.isWireMeshConfiguration ? this.wirePostType : '';
    const postState = this.buildPostState(
      products,
      accessoryPostPositions,
      requiredPostColor,
      requiredPostLengthMm,
      requiredWirePostType,
    );
    const gatePostState = this.buildPostState(
      products,
      gatePostPositions,
      requiredPostColor,
      requiredPostLengthMm,
      '',
      true,
    );
    gatePostState.missingByLength.forEach((count, length) => {
      postState.missingByLength.set(length, (postState.missingByLength.get(length) || 0) + count);
    });
    postState.products = [...postState.products, ...gatePostState.products];
    postState.eligibleProducts = [...postState.eligibleProducts, ...gatePostState.eligibleProducts];
    postState.normalEligibleProducts = postState.eligibleProducts.filter((item) => !item.isGatePost);
    postState.gateEligibleProducts = gatePostState.eligibleProducts;
    postState.requiredPieces += gatePostState.requiredPieces;
    postState.selectedPieces += gatePostState.selectedPieces;
    postState.fills = [...postState.fills, ...gatePostState.fills];
    postState.missing += gatePostState.missing;
    postState.unfillablePieces += gatePostState.unfillablePieces;
    const postCarrier = this.buildPostCarrierState(
      products,
      this.isInsertFenceConfiguration && this.postLengthMm === 1800
        ? postPositions.length + gatePostPositions.length
        : 0
    );
    postState.carrier = postCarrier;
    postState.fills = [...postState.fills, ...postCarrier.fills];
    postState.missing += postCarrier.missing;
    postState.unfillablePieces += postCarrier.unfillablePieces;
    this.accessoryState = {
      post: postState,
      postCarrier,
      bracket: this.buildBracketState(products, expanded.filter((item) => (
        item.shape !== 'wpc-panel'
        && item.shape !== 'wpc-gate'
        && !item.isLightline
        && !item.bracketsIncluded
        && !item.visualStyles.includes('wire-mesh')
        && !item.visualStyles.includes('glass')
      )).length),
      startEnd: this.buildStartEndState(products, expanded, requiredWpcColor),
      // Steckzaunpfosten werden je nach gewählter Länge direkt einbetoniert oder
      // mit dem eigenen Pfostenträger montiert. Die allgemeine Fundamentauswahl
      // (H-Pfostenträger + Durchgangsschrauben) gilt nur für andere Zaunsysteme.
      foundation: this.buildFoundationState(
        products,
        this.isInsertFenceConfiguration || this.isWireMeshConfiguration || this.isLightlineConfiguration
          ? 0
          : postPositions.length,
      ),
    };

    // Gesamtlänge inkl. Pfosten: Breite aus Produktdaten, sonst 9 cm als üblicher Pfostenquerschnitt
    const postWidth = this.accessoryState.post.products.find((item) => item.width)?.width || DEFAULT_POST_WIDTH_CM;
    const totalWidth = expanded.reduce((sum, item) => sum + item.width, 0) + postPositions.length * postWidth;
    const totalPrice = products.reduce((sum, item) => sum + item.quantity * item.price, 0);
    return { products, expanded, postPositions, totalWidth, totalPrice };
  }

  renderSummary({ products, totalWidth, totalPrice }) {
    if (this.deliveryTimeOutput) {
      const deliveryTimes = [...new Set(products
        .filter((item) => item.quantity > 0)
        .map((item) => item.deliveryTime || this.deliveryTimeOutput.dataset.defaultDeliveryTime))];
      this.deliveryTimeOutput.textContent = deliveryTimes.length
        ? deliveryTimes.join(' / ')
        : this.deliveryTimeOutput.dataset.defaultDeliveryTime;
    }
    this.lengthOutput.textContent = `ca. ${(totalWidth / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m`;
    if (this.priceOutput) {
      this.priceOutput.textContent = (totalPrice / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
    }
  }

  renderChecks({ expanded }) {
    if (this.checksContainer) this.checksContainer.hidden = !expanded.length;
    if (this.wpcColorPicker) {
      this.wpcColorPicker.hidden = !expanded.length
        || (!this.isWpcConfiguration && !this.isGlassConfiguration);
    }
    if (this.frameColorLabel) {
      this.frameColorLabel.textContent = this.isGlassConfiguration ? 'Pfostenfarbe' : 'Pfosten/Rahmen';
    }
    if (this.frameColorOptions) {
      this.frameColorOptions.setAttribute(
        'aria-label',
        this.isGlassConfiguration ? 'Pfostenfarbe wählen' : 'Pfosten- und Rahmenfarbe wählen',
      );
    }
    if (this.wpcPanelColorPicker) {
      this.wpcPanelColorPicker.hidden = !expanded.length
        || (!this.isWpcConfiguration && !this.isWireMeshConfiguration);
    }
    if (this.wpcPanelColorLabel) {
      this.wpcPanelColorLabel.textContent = this.isWireMeshConfiguration ? 'Farbe' : 'Füllung';
    }
    if (this.wpcPanelColorOptions) {
      this.wpcPanelColorOptions.setAttribute(
        'aria-label',
        this.isWireMeshConfiguration ? 'Farbe der Doppelstabmatten wählen' : 'Feldfarbe wählen',
      );
    }
    if (this.postLengthPicker) this.postLengthPicker.hidden = !expanded.length || !this.isInsertFenceConfiguration;
    if (this.postLengthSelect) this.postLengthSelect.value = String(this.postLengthMm);
    if (this.wirePostTypePicker) {
      this.wirePostTypePicker.hidden = !expanded.length
        || !this.isWireMeshConfiguration
        || this.availableWirePostTypes.size < 2;
    }
    this.wirePostTypeButtons.forEach((button) => {
      button.hidden = !this.availableWirePostTypes?.has(button.dataset.wirePostType);
      button.setAttribute('aria-pressed', String(button.dataset.wirePostType === this.wirePostType));
    });
    this.wpcColorButtons.forEach((button) => {
      button.hidden = !this.availableWpcFrameColors?.has(button.dataset.wpcColor);
      button.setAttribute('aria-pressed', String(button.dataset.wpcColor === this.wpcFrameColor));
    });
    this.wpcPanelColorButtons.forEach((button) => {
      button.hidden = !this.availableWpcPanelColors?.has(button.dataset.wpcPanelColor);
      button.setAttribute('aria-pressed', String(button.dataset.wpcPanelColor === this.wpcPanelColor));
    });
    const checkDescriptors = {
      post: {
        missingPieces: (state) => [...state.missingByLength.values()].reduce((sum, count) => sum + count, 0)
          + Math.max(0, state.carrier.requiredPieces - state.carrier.selectedPieces),
      },
      bracket: {
        missingPieces: (state) => Math.max(0, state.requiredPieces - state.selectedPieces),
      },
      startEnd: {
        missingPieces: (state) => Math.max(0, state.requiredPieces - state.selectedPieces),
      },
      foundation: {
        missingPieces: (state) => Math.max(0, state.anchors.requiredPieces - state.anchors.selectedPieces)
          + Math.max(0, state.bolts.requiredPieces - state.bolts.selectedPieces),
      },
    };
    Object.entries(checkDescriptors).forEach(([type, describe]) => {
      const row = this.checkRows?.[type];
      if (!row) return;
      const state = this.accessoryState[type];
      const shouldShowWithoutProduct = type === 'bracket' || type === 'startEnd';
      row.hidden = !expanded.length || state.requiredPieces === 0 || (!state.products.length && !shouldShowWithoutProduct);
      const missingPieces = describe.missingPieces(state);
      const complete = missingPieces === 0;
      row.classList.toggle('is-ok', complete);
      row.classList.toggle('is-missing', !complete);
      const button = row.querySelector('[data-add-required]');
      if (button) button.hidden = complete || !state.missing;
    });
  }

  renderScene({ expanded, postPositions }) {
    this.empty.hidden = expanded.length > 0;
    this.scene.replaceChildren();
    this.scene.style.removeProperty('--post-ground-depth');
    if (!expanded.length) return;

    const postState = this.accessoryState.post;
    const bracketState = this.accessoryState.bracket;
    const bracketOffsets = new Map();
    let bracketOffset = 0;
    expanded.forEach((item) => {
      if (
        item.shape === 'wpc-panel'
        || item.shape === 'wpc-gate'
        || item.bracketsIncluded
        || item.visualStyles.includes('wire-mesh')
        || item.visualStyles.includes('glass')
      ) return;
      bracketOffsets.set(item.configKey, bracketOffset);
      bracketOffset += 4;
    });
    const maxHeight = Math.max(...expanded.map((item) => item.height), SCENE_REFERENCE_HEIGHT_CM);
    const panelHeight = (item) => Math.max(MIN_PANEL_HEIGHT_PX, cmToPx(item.height));
    const wpcFieldVisualHeight = Math.max(0, ...expanded
      .filter((item) => item.shape === 'wpc-panel')
      .map((item) => panelHeight(item)));
    const visualPanelHeight = (item) => item.shape === 'wpc-gate' && wpcFieldVisualHeight
      ? Math.max(panelHeight(item), wpcFieldVisualHeight)
      : panelHeight(item);
    // Pfostenhöhe pro Position: passend zum höchsten angrenzenden Element statt pauschal zum höchsten der Konfiguration.
    // An WPC-Toren folgt auch der Pfosten der rein optisch angeglichenen Feldhöhe.
    const postHeightAt = (index) => {
      const adjacentGate = [expanded[index - 1], expanded[index]].some((item) => item?.shape === 'wpc-gate');
      const visualGateHeight = adjacentGate ? wpcFieldVisualHeight : 0;
      return Math.max(MIN_POST_HEIGHT_PX, cmToPx(postPositions[index] || maxHeight), visualGateHeight);
    };
    const configuredPostHeight = this.isInsertFenceConfiguration && this.postLengthMm > 1800
      ? cmToPx(this.postLengthMm / 10)
      : 0;
    const postEmbedDepthAt = (index) => configuredPostHeight
      ? Math.max(0, configuredPostHeight - postHeightAt(index))
      : 0;
    const maxPostEmbedDepth = Math.max(0, ...postPositions.map((_, index) => postEmbedDepthAt(index)));
    this.scene.style.setProperty('--post-ground-depth', `${maxPostEmbedDepth}px`);
    const bracketTop = (item, edge, postHeight) => {
      let inset = postHeight - panelHeight(item) + BRACKET_INSET_OFFSET_PX;
      if (isSlopeShape(item.shape) && edge !== item.slopeHighSide) {
        inset += panelHeight(item) * Math.max(0, 1 - item.lowHeight / item.height);
      }
      if (isArchConvexShape(item.shape)) {
        inset += panelHeight(item) * Math.max(0, 1 - item.lowHeight / item.height);
      }
      if (item.shape === 'step' && edge === 'right') inset += panelHeight(item) * 0.18;
      return Math.max(MIN_BRACKET_INSET_PX, inset);
    };
    const addPost = (index) => {
      const post = document.createElement('span');
      const fenceLineHeight = postHeightAt(index);
      const postEmbedDepth = postEmbedDepthAt(index);
      const postHeight = fenceLineHeight + postEmbedDepth;
      post.className = `fence-pixel-post${postState.assignedPositions.has(index) ? '' : ' is-missing'}`;
      if (postEmbedDepth) post.classList.add('fence-pixel-post--embedded');
      const adjacentWpc = [expanded[index - 1], expanded[index]].find((item) => item?.shape === 'wpc-panel' || item?.shape === 'wpc-gate');
      if (adjacentWpc) {
        post.classList.add('fence-pixel-post--wpc');
        const postFrameColor = this.isWpcConfiguration ? this.wpcFrameColor : adjacentWpc.frameColor;
        if (postFrameColor) post.classList.add(`fence-pixel-post--frame-${postFrameColor}`);
      }
      const adjacentGlass = [expanded[index - 1], expanded[index]]
        .find((item) => item?.visualStyles.includes('glass'));
      if (!adjacentWpc && adjacentGlass) {
        post.classList.add('fence-pixel-post--metal');
        if (this.wpcFrameColor) post.classList.add(`fence-pixel-post--frame-${this.wpcFrameColor}`);
      }
      const adjacentWireMesh = [expanded[index - 1], expanded[index]].find((item) => item?.visualStyles.includes('wire-mesh'));
      if (adjacentWireMesh) {
        post.classList.add('fence-pixel-post--wire-mesh');
        if (this.wirePostType) post.classList.add(`fence-pixel-post--wire-${this.wirePostType}`);
        ['wire-green', 'wire-anthracite', 'wire-galvanized']
          .filter((style) => adjacentWireMesh.visualStyles.includes(style))
          .forEach((style) => post.classList.add(`fence-pixel-post--${style}`));
      }
      const adjacentMetalPanel = [expanded[index - 1], expanded[index]]
        .some((item) => item?.visualStyles.includes('metal'));
      if (!adjacentWpc && !adjacentGlass && !adjacentWireMesh && adjacentMetalPanel) {
        post.classList.add('fence-pixel-post--metal');
      }
      const adjacentWhitePanel = [expanded[index - 1], expanded[index]].some((item) => item?.panelColor === 'weiss');
      if (!adjacentWpc && !adjacentMetalPanel && adjacentWhitePanel) post.classList.add('fence-pixel-post--white');
      post.style.setProperty('--post-height', `${postHeight}px`);
      post.style.setProperty('--post-embed-depth', `${postEmbedDepth}px`);
      post.setAttribute('aria-hidden', 'true');
      const usesInsertFenceCarrier = this.isInsertFenceConfiguration && this.postLengthMm === 1800;
      const usesFoundationAnchor = this.accessoryState.foundation.anchors.requiredPieces > 0;
      if (usesFoundationAnchor || usesInsertFenceCarrier) {
        const selectedAnchors = usesInsertFenceCarrier
          ? this.accessoryState.postCarrier.selectedPieces
          : this.accessoryState.foundation.anchors.selectedPieces;
        const anchor = document.createElement('span');
        anchor.className = `fence-pixel-anchor${index < selectedAnchors ? '' : ' is-missing'}`;
        post.append(anchor);
      }
      const addBracket = (side, position, bracketIndex, item, edge) => {
        const bracket = document.createElement('b');
        bracket.className = `fence-pixel-bracket fence-pixel-bracket--${side}-${position}`;
        if (bracketIndex >= bracketState.selectedPieces) bracket.classList.add('is-missing');
        if (position === 'top') bracket.style.setProperty('--bracket-top', `${bracketTop(item, edge, fenceLineHeight)}px`);
        post.append(bracket);
      };
      const leftItem = expanded[index - 1];
      const leftOffset = leftItem ? bracketOffsets.get(leftItem.configKey) : undefined;
      if (leftItem && leftOffset !== undefined) {
        addBracket('left', 'top', leftOffset + 1, leftItem, 'right');
        addBracket('left', 'bottom', leftOffset + 3, leftItem, 'right');
      }
      const rightItem = expanded[index];
      const rightOffset = rightItem ? bracketOffsets.get(rightItem.configKey) : undefined;
      if (rightItem && rightOffset !== undefined) {
        addBracket('right', 'top', rightOffset, rightItem, 'left');
        addBracket('right', 'bottom', rightOffset + 2, rightItem, 'left');
      }
      this.scene.append(post);
    };

    addPost(0);
    expanded.forEach((item, index) => {
      const panel = document.createElement('span');
      panel.className = `fence-pixel-panel fence-pixel-panel--${item.shape}`;
      item.visualStyles.forEach((style) => panel.classList.add(`fence-pixel-panel--visual-${style}`));
      if ((item.shape === 'gate' || item.shape.startsWith('picket-gate')) && item.width >= 200) {
        panel.classList.add('fence-pixel-panel--double-gate');
      }
      if (item.panelColor) panel.classList.add(`fence-pixel-panel--color-${item.panelColor}`);
      const renderedFrameColor = this.isWpcConfiguration && item.shape === 'wpc-panel'
        ? this.wpcFrameColor
        : item.frameColor;
      if (renderedFrameColor) panel.classList.add(`fence-pixel-panel--frame-${renderedFrameColor}`);
      if (isSlopeShape(item.shape)) panel.classList.add(`fence-pixel-panel--high-${item.slopeHighSide}`);
      panel.dataset.fenceKey = item.configKey;
      panel.tabIndex = 0;
      panel.setAttribute('aria-label', `${item.title}, zum Sortieren ziehen oder Pfeiltasten verwenden`);
      const renderedWidth = Math.max(MIN_PANEL_WIDTH_PX, cmToPx(item.width));
      const renderedHeight = visualPanelHeight(item);
      panel.style.setProperty('--panel-width', `${renderedWidth}px`);
      panel.style.setProperty('--panel-height', `${renderedHeight}px`);
      if (hasArchDrop(item.shape)) {
        panel.style.setProperty('--arch-drop', `${Math.min(35, Math.max(8, (1 - item.lowHeight / item.height) * 100))}%`);
      }
      if (isSlopeShape(item.shape)) {
        const slopeDrop = Math.min(0.75, Math.max(0.1, 1 - item.lowHeight / item.height));
        const slopeAngle = Math.atan((renderedHeight * slopeDrop) / renderedWidth) * (180 / Math.PI);
        panel.style.setProperty('--slope-drop', `${slopeDrop * 100}%`);
        panel.style.setProperty('--slope-angle', `${slopeAngle}deg`);
        panel.style.setProperty('--slope-angle-reverse', `${-slopeAngle}deg`);
      }
      panel.title = `${item.title} – ${item.width} × ${item.height} cm`;
      panel.innerHTML = '<i></i>';
      const removeButton = document.createElement('button');
      removeButton.className = 'fence-element-remove';
      removeButton.type = 'button';
      removeButton.dataset.removeFenceItem = item.configKey;
      removeButton.setAttribute('aria-label', `${item.title} entfernen`);
      removeButton.textContent = '×';
      panel.append(removeButton);
      this.scene.append(panel);
      addPost(index + 1);
    });
  }

  render() {
    const state = this.computeState();
    this.renderSummary(state);
    this.renderChecks(state);
    this.updateCartButton();
    this.renderScene(state);
  }

  alignSlopes(elements) {
    if (!elements.length) return;
    const states = elements.map((item) => (isSlopeShape(item.shape) ? ['left', 'right'] : ['fixed']));
    const costs = [];
    const previousStates = [];
    states.forEach((itemStates, index) => {
      costs[index] = {};
      previousStates[index] = {};
      itemStates.forEach((state) => {
        if (index === 0) {
          costs[index][state] = 0;
          return;
        }
        let bestCost = Infinity;
        let bestPrevious = states[index - 1][0];
        states[index - 1].forEach((previousState) => {
          const previousEdges = edgesForShape(elements[index - 1], previousState);
          const currentEdges = edgesForShape(elements[index], state);
          const cost = costs[index - 1][previousState] + Math.abs(previousEdges[1] - currentEdges[0]);
          if (cost < bestCost) {
            bestCost = cost;
            bestPrevious = previousState;
          }
        });
        costs[index][state] = bestCost;
        previousStates[index][state] = bestPrevious;
      });
    });
    const lastIndex = elements.length - 1;
    let selectedState = states[lastIndex].reduce((best, state) => (costs[lastIndex][state] < costs[lastIndex][best] ? state : best), states[lastIndex][0]);
    for (let index = lastIndex; index >= 0; index -= 1) {
      if (isSlopeShape(elements[index].shape)) elements[index].slopeHighSide = selectedState;
      selectedState = previousStates[index][selectedState];
    }
  }
}

if (!customElements.get('fence-configurator')) customElements.define('fence-configurator', FenceConfigurator);
